import copy from '../copy/en/signing.json' with { type: 'json' };
/**
 * Debug autofill for manual testing. Activated ONLY when the page URL carries
 * the `?mock=1` (or bare `?mock`) flag; normal loads are completely unaffected.
 *
 * It fills Information + Practice with randomized but SERVER-VALID data and
 * stops there — it never accepts consent, draws a signature, submits, or
 * advances steps. Masked fields (phone, ZIP) are driven through the real mask by
 * dispatching native `input` events, so the displayed value formats and the
 * mask's `getRaw()` still yields normalized digits for submission.
 */

import { syncPracticeControls } from "./controls.js";

export interface MockSigner {
  signerName: string;
  practiceName: string;
  legalCompanyName: string;
  email: string;
  phone: string; // 10 raw digits
  addressStreet: string;
  city: string;
  state: string;
  zip: string; // 5 or 9 raw digits
  signerTitle: string;
  signerCapacity: string;
  mobilePhone: string;
  pocFirstName: string;
  pocLastName: string;
  pocEmail: string;
  entityType: string;
  locationCount: string;
  practiceType: string;
  npi: string;
  website: string;
  ownership: string;
  yearsInPractice: string;
  ownerDentistCount: string;
  dentistCount: string;
  hygienistCount: string;
  operatoryCount: string;
  fteCount: string;
  activePatientCount: string;
  monthlyVisits: string;
  annualRevenueRange: string;
  annualCardVolumeRange: string;
  cardProcessingFeeRange: string;
  intraoralScanner: string;
  facility: string;
  consideringSellOrAcquisition: string;
  anticipatingAcquisition: string;
  practiceSoftware: string;
  practiceSoftwareOther: string;
  daysOpen: string;
  typicalHours: string;
  insuranceCarriers: string;
  insuranceOther: string;
  procedures: string;
  proceduresOther: string;
}

/** True when the URL query requests mock autofill (`?mock`, `?mock=1`, `?mock=true`). */
export function isMockRequested(search: string): boolean {
  const params = new URLSearchParams(search);
  if (!params.has("mock")) return false;
  const value = params.get("mock");
  return (
    value === null ||
    value === "" ||
    value === "1" ||
    value.toLowerCase() === "true"
  );
}

const FIRST_NAMES = [
  "Jane", "John", "Maria", "David", "Priya", "Chen", "Aisha", "Liam",
  "Sofia", "Omar", "Grace", "Noah", "Elena", "Marcus", "Yuki",
];
const LAST_NAMES = [
  "Doe", "Smith", "Nguyen", "Patel", "Garcia", "Kim", "Johnson", "Okafor",
  "Rossi", "Hernandez", "Weber", "Silva", "Ivanov", "Cohen", "Tanaka",
];
const PRACTICE_PREFIXES = [
  "Bright Smiles", "Gentle Care", "Lakeside", "Summit", "Harbor", "Maple",
  "Riverbend", "Cornerstone", "Evergreen", "Downtown", "Sunrise", "Oakwood",
];
const PRACTICE_KINDS = ["Dental", "Dental Group", "Family Dentistry", "Orthodontics", "Dental Care"];
const COMPANY_SUFFIXES = ["LLC", "PLLC", "Inc.", "PC", "Group LLC"];
const EMAIL_DOMAINS = ["example.com", "testmail.com", "mailinator.com", "acme-dental.example"];
const STREET_NAMES = ["Main", "Oak", "Maple", "2nd", "Elm", "Cedar", "Park", "Washington", "Lake", "Pine"];
const STREET_TYPES = ["St", "Ave", "Blvd", "Rd", "Ln", "Dr"];
const CITIES = ["Oakland", "Austin", "Denver", "Portland", "Madison", "Tampa", "Boise", "Raleigh", "Reno", "Akron"];
const STATES = ["CA", "TX", "CO", "OR", "WI", "FL", "ID", "NC", "NV", "OH", "NY", "WA", "GA", "AZ"];
const TITLES = ["Owner", "Managing Member", "President", "CEO", "Practice Manager", "Partner"];
const CAPACITIES = [
  "On behalf of the Practice",
  "Authorized signer",
  "Managing Member",
  "Owner / Principal",
];
const ENTITY_TYPES = [
  "Sole proprietorship", "Partnership", "LLC", "PLLC",
  "Professional corporation (PC)", "S corporation",
];
const PRACTICE_TYPES = [
  "General", "Pediatric", "Ortho", "OMS", "Periodontics", "Endo", "Multi-specialty",
];
const OWNERSHIP = ["Independently owned", "Group", "Equity", "DSO"];
const YEARS = ["Less than 1", "1–3", "3–5", "5–10", "10–20", "20+"];
const VISITS = ["Under 300", "300–600", "600–900", "900–1200", "1200+"];
const REVENUE = ["Under $500K", "$500K–$999K", "$1M–$1.99M", "$2M–$4.99M", "$5M+"];
const CARD_VOLUME = ["Under $250K", "$250K–$499K", "$500K–$999K", "$1M–$1.99M", "$2M+"];
const PROCESSING_FEES = ["Under 2%", "2%–2.49%", "2.5%–2.99%", "3%–3.49%", "3.5%+", "Not sure"];
const SCANNERS = ["None", "iTero", "3Shape TRIOS", "Dentsply Sirona Primescan", "Medit", "Carestream", "Other", "Not sure"];
const SOFTWARE = ["Dentrix", "Eaglesoft", "Open Dental", "Curve Dental", "SoftDent", "Athenahealth"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const INSURANCE = [
  "Delta Dental", "Cigna", "Aetna", "MetLife", "UnitedHealthcare",
  "Guardian", "Humana", "Blue Cross Blue Shield", "Medicaid",
];
const PROCEDURES = [
  "General", "Hygiene", "Implants", "Ortho", "Oral surgery", "Endo", "Perio", "Cosmetics",
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function pickFew<T>(arr: T[], min: number, max: number): T[] {
  const n = min + Math.floor(Math.random() * (max - min + 1));
  const copy = [...arr];
  const out: T[] = [];
  for (let i = 0; i < n && copy.length; i++) {
    out.push(copy.splice(Math.floor(Math.random() * copy.length), 1)[0]);
  }
  return out;
}

function digits(n: number): string {
  let out = "";
  for (let i = 0; i < n; i++) out += Math.floor(Math.random() * 10);
  return out;
}

/** 10-digit US phone with NXX area/exchange (first digit 2-9). */
function randomPhone(): string {
  const nxx = () => String(2 + Math.floor(Math.random() * 8)) + digits(2);
  return nxx() + nxx() + digits(4);
}

function randomZip(): string {
  const base = digits(5);
  return Math.random() < 0.4 ? base + digits(4) : base;
}

/** Generate randomized but server-valid signer data (masked fields as raw digits). */
export function generateMockSigner(): MockSigner {
  const first = pick(FIRST_NAMES);
  const last = pick(LAST_NAMES);
  const middleInitial = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  const practice = `${pick(PRACTICE_PREFIXES)} ${pick(PRACTICE_KINDS)}`;
  const signerName = `${first} ${middleInitial}. ${last}`;
  const legalCompanyName = `${practice} ${pick(COMPANY_SUFFIXES)}`;
  const pocFirst = pick(FIRST_NAMES);
  const pocLast = pick(LAST_NAMES);
  const weekdays = DAYS.slice(0, 5);
  const openDays = Math.random() < 0.25 ? [...weekdays, "Sat"] : weekdays;

  return {
    signerName,
    practiceName: practice,
    legalCompanyName,
    email: `${first}.${last}${10 + Math.floor(Math.random() * 89)}@${pick(EMAIL_DOMAINS)}`.toLowerCase(),
    phone: randomPhone(),
    addressStreet: `${100 + Math.floor(Math.random() * 9800)} ${pick(STREET_NAMES)} ${pick(STREET_TYPES)}, Suite ${1 + Math.floor(Math.random() * 40)}`,
    city: pick(CITIES),
    state: pick(STATES),
    zip: randomZip(),
    signerTitle: pick(TITLES),
    signerCapacity: pick(CAPACITIES),
    mobilePhone: randomPhone(),
    pocFirstName: pocFirst,
    pocLastName: pocLast,
    pocEmail: `${pocFirst}.${pocLast}${10 + Math.floor(Math.random() * 89)}@${pick(EMAIL_DOMAINS)}`.toLowerCase(),
    entityType: pick(ENTITY_TYPES),
    locationCount: String(1 + Math.floor(Math.random() * 4)),
    practiceType: pick(PRACTICE_TYPES),
    npi: digits(10),
    website: `www.${practice.replace(/\s+/g, "").toLowerCase()}.example`,
    ownership: pick(OWNERSHIP),
    yearsInPractice: pick(YEARS),
    ownerDentistCount: String(1 + Math.floor(Math.random() * 3)),
    dentistCount: String(1 + Math.floor(Math.random() * 6)),
    hygienistCount: String(1 + Math.floor(Math.random() * 8)),
    operatoryCount: String(3 + Math.floor(Math.random() * 10)),
    fteCount: String(4 + Math.floor(Math.random() * 20)),
    activePatientCount: String(800 + Math.floor(Math.random() * 4200)),
    monthlyVisits: pick(VISITS),
    annualRevenueRange: pick(REVENUE),
    annualCardVolumeRange: pick(CARD_VOLUME),
    cardProcessingFeeRange: pick(PROCESSING_FEES),
    intraoralScanner: pick(SCANNERS),
    facility: pick(["Lease", "Own"] as const),
    consideringSellOrAcquisition: pick(["Yes", "No", "Maybe"] as const),
    anticipatingAcquisition: pick(["Yes", "No"] as const),
    practiceSoftware: pick(SOFTWARE),
    practiceSoftwareOther: "",
    daysOpen: openDays.join(", "),
    typicalHours: "Mon–Thu 8–5, Fri 8–2",
    insuranceCarriers: pickFew(INSURANCE, 2, 5).join(", "),
    insuranceOther: "",
    procedures: pickFew(PROCEDURES, 3, 6).join(", "),
    proceduresOther: "",
  };
}

function setControlValue(
  form: HTMLFormElement,
  name: string,
  value: string,
  eventType: "input" | "change",
): void {
  const el = form.elements.namedItem(name);
  if (!el) return;
  if (el instanceof RadioNodeList) {
    el.value = value;
    return;
  }
  if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
    el.value = value;
    el.dispatchEvent(new Event(eventType, { bubbles: true }));
  }
}

function showMockBadge(): void {
  if (document.getElementById("mockBadge")) return;
  const badge = document.createElement("div");
  badge.id = "mockBadge";
  badge.className = "mock-badge";
  badge.textContent = copy.mockBadge;
  badge.title = copy.mockHelp;
  document.body.appendChild(badge);
}

/**
 * Fill Information + Practice with the given (or freshly generated) mock data.
 * Drives masked inputs (phone, ZIP) through their `input` handlers so the
 * displayed value formats and the mask records normalized digits for getRaw().
 */
export function fillMockData(
  form: HTMLFormElement,
  data: MockSigner = generateMockSigner(),
): MockSigner {
  setControlValue(form, "signerName", data.signerName, "input");
  setControlValue(form, "practiceName", data.practiceName, "input");
  setControlValue(form, "legalCompanyName", data.legalCompanyName, "input");
  setControlValue(form, "email", data.email, "input");
  setControlValue(form, "addressStreet", data.addressStreet, "input");
  setControlValue(form, "city", data.city, "input");
  setControlValue(form, "state", data.state, "input");
  setControlValue(form, "signerTitle", data.signerTitle, "input");
  setControlValue(form, "signerCapacity", data.signerCapacity, "input");
  setControlValue(form, "phone", data.phone, "input");
  setControlValue(form, "zip", data.zip, "input");
  setControlValue(form, "mobilePhone", data.mobilePhone, "input");
  setControlValue(form, "pocFirstName", data.pocFirstName, "input");
  setControlValue(form, "pocLastName", data.pocLastName, "input");
  setControlValue(form, "pocEmail", data.pocEmail, "input");
  setControlValue(form, "entityType", data.entityType, "change");
  setControlValue(form, "locationCount", data.locationCount, "input");
  setControlValue(form, "practiceType", data.practiceType, "change");
  setControlValue(form, "npi", data.npi, "input");
  setControlValue(form, "website", data.website, "input");
  setControlValue(form, "ownership", data.ownership, "change");
  setControlValue(form, "yearsInPractice", data.yearsInPractice, "change");
  setControlValue(form, "ownerDentistCount", data.ownerDentistCount, "input");
  setControlValue(form, "dentistCount", data.dentistCount, "input");
  setControlValue(form, "hygienistCount", data.hygienistCount, "input");
  setControlValue(form, "operatoryCount", data.operatoryCount, "input");
  setControlValue(form, "fteCount", data.fteCount, "input");
  setControlValue(form, "activePatientCount", data.activePatientCount, "input");
  setControlValue(form, "monthlyVisits", data.monthlyVisits, "change");
  setControlValue(form, "annualRevenueRange", data.annualRevenueRange, "change");
  setControlValue(form, "annualCardVolumeRange", data.annualCardVolumeRange, "change");
  setControlValue(form, "cardProcessingFeeRange", data.cardProcessingFeeRange, "change");
  setControlValue(form, "intraoralScanner", data.intraoralScanner, "change");
  setControlValue(form, "facility", data.facility, "change");
  setControlValue(form, "consideringSellOrAcquisition", data.consideringSellOrAcquisition, "change");
  setControlValue(form, "anticipatingAcquisition", data.anticipatingAcquisition, "change");
  setControlValue(form, "practiceSoftware", data.practiceSoftware, "change");
  setControlValue(form, "daysOpen", data.daysOpen, "change");
  setControlValue(form, "typicalHours", data.typicalHours, "input");
  setControlValue(form, "insuranceCarriers", data.insuranceCarriers, "change");
  setControlValue(form, "procedures", data.procedures, "change");

  syncPracticeControls(form);
  showMockBadge();
  return data;
}
